import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import gsap from "gsap";

/**
 * SignalLockIntro — PARABOLUM v1.0 RC intro.
 *
 * Concept (kept exactly):
 *   The wordmark appears as displaced fragments of PARABOLUM, then assembles
 *   piece-by-piece into the full wordmark. Alien-inspired, premium, restrained.
 *
 * Handoff (per direction — "a clean fade is better than a bad shared-element
 * transition"): after assembly + hold, the intro fades out smoothly, then the
 * hero fades in smoothly. No motion attempt, no measurement, no swap — and
 * therefore no micro-teleport.
 *
 * Timeline:
 *   0.0  start — pure black, calm starfield, fragments invisible (no flash)
 *   0.3  fragments fade in (still displaced)
 *   1.1  fragments snap together — full PARABOLUM
 *   2.0  hold 0.6s
 *   2.6  intro overlay fades out (0.55s power2.inOut)
 *   2.9  hero fades in (subtitle/CTAs/black hole during the overlap,
 *        hero h1 from t=2.9s onward over 0.65s)
 *   3.15 overlay unmounted
 */

const WORD = "PARABOLUM";

// viewBox + text geometry
const VB_W = 1200;
const VB_H = 280;
const TEXT_LEN = 1120;

// fragment grid
const COLS = 9;
const ROWS = 3;
const FX0 = 30;
const FY0 = 44;
const FW = 1140;
const FH = 186;
const CW = FW / COLS;
const CH = FH / ROWS;

export default function SignalLockIntro({ onReveal, onComplete }) {
  const [show, setShow] = useState(true);
  const reduced = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    [],
  );

  const rootRef = useRef(null);
  const bgRef = useRef(null);
  const starsRef = useRef(null);
  const svgRef = useRef(null);
  const fragRefs = useRef([]);
  const revealedRef = useRef(false);
  const completedRef = useRef(false);
  const onRevealRef = useRef(onReveal);
  const onCompleteRef = useRef(onComplete);
  onRevealRef.current = onReveal;
  onCompleteRef.current = onComplete;

  const fragments = useMemo(() => {
    const out = [];
    for (let r = 0; r < ROWS; r += 1) {
      for (let c = 0; c < COLS; c += 1) {
        out.push({
          id: `slk-${r}-${c}`,
          x: FX0 + c * CW,
          y: FY0 + r * CH,
          w: CW + 0.5,
          h: CH + 0.5,
          dx: (Math.random() * 2 - 1) * 60,
          dy: (Math.random() * 2 - 1) * 42,
        });
      }
    }
    return out;
  }, []);

  const stars = useMemo(
    () =>
      Array.from({ length: 22 }, () => ({
        left: Math.random() * 100,
        top: Math.random() * 100,
        size: Math.random() < 0.85 ? 1 : 2,
        opacity: 0.1 + Math.random() * 0.22,
      })),
    [],
  );

  const fireReveal = () => {
    if (revealedRef.current) return;
    revealedRef.current = true;
    onRevealRef.current?.();
  };
  const fireComplete = () => {
    if (completedRef.current) return;
    completedRef.current = true;
    onCompleteRef.current?.();
  };

  useLayoutEffect(() => {
    const root = rootRef.current;
    const svg = svgRef.current;
    const frags = fragRefs.current.filter(Boolean);
    if (!root || !svg || frags.length === 0) return undefined;

    const ctx = gsap.context(() => {
      // ------- INITIAL STATE (synchronous — pre-paint) -------
      gsap.set(svg, { opacity: 1 });
      if (bgRef.current) gsap.set(bgRef.current, { opacity: 1 });
      if (starsRef.current) gsap.set(starsRef.current, { opacity: 1 });
      frags.forEach((el, i) => {
        gsap.set(el, {
          x: fragments[i].dx,
          y: fragments[i].dy,
          opacity: 0,
        });
      });

      // ------- REDUCED MOTION -------
      if (reduced) {
        gsap.set(frags, { x: 0, y: 0, opacity: 1 });
        const tlr = gsap.timeline();
        tlr.add(fireReveal, 0.2);
        tlr.add(fireComplete, 0.6);
        tlr.to(
          root,
          {
            opacity: 0,
            duration: 0.5,
            ease: "power2.out",
            onComplete: () => setShow(false),
          },
          0.6,
        );
        return;
      }

      // ------- TIMELINE — assembly + hold + clean fade hand-off -------
      const tl = gsap.timeline();

      // Phase A — fragments appear, still displaced (0.30–1.00).
      tl.to(
        frags,
        {
          opacity: 0.82,
          duration: 0.6,
          ease: "power1.out",
          stagger: { each: 0.012, from: "random" },
        },
        0.3,
      );

      // Phase B — segmented lock: fragments snap to their places (1.05–1.95).
      tl.to(
        frags,
        {
          x: 0,
          y: 0,
          opacity: 1,
          duration: 0.85,
          ease: "power3.out",
          stagger: { each: 0.014, from: "random" },
        },
        1.05,
      );

      // Phase C — confident hold (~0.6s).

      // Phase D — fire reveal + complete just as the intro starts to dissolve.
      // The hero's <h1>, subtitle, CTAs and black hole all fade in at this
      // point (Hero.jsx animates opacity from 0→1 gated by titleReady).
      tl.add(fireReveal, 2.55);
      tl.add(fireComplete, 2.55);

      // Background + stars fade out (slightly faster than overlay so the
      // hero materialises behind for the last beat of the cross-fade).
      tl.to(bgRef.current, { opacity: 0, duration: 0.55, ease: "power2.inOut" }, 2.55);
      tl.to(starsRef.current, { opacity: 0, duration: 0.4, ease: "power2.inOut" }, 2.55);

      // Overlay fades out — smooth, no movement.
      tl.to(
        root,
        {
          opacity: 0,
          duration: 0.55,
          ease: "power2.inOut",
          onComplete: () => setShow(false),
        },
        2.55,
      );
    }, rootRef);

    return () => ctx.revert();
  }, [reduced, fragments]);

  if (!show) return null;

  // Text styling tuned to the hero typography (Cabinet Grotesk 800,
  // letter-spacing matching display-hero). The intro and hero look like the
  // same wordmark — but we are NOT trying to morph between them.
  const textProps = {
    x: VB_W / 2,
    y: 196,
    textAnchor: "middle",
    textLength: TEXT_LEN,
    lengthAdjust: "spacingAndGlyphs",
    fill: "#ffffff",
    style: {
      fontFamily: '"Cabinet Grotesk", "Inter", system-ui, sans-serif',
      fontWeight: 800,
      fontSize: "200px",
      letterSpacing: "-0.045em",
    },
  };

  return (
    <div
      ref={rootRef}
      className="fixed inset-0 z-[300] flex items-center justify-center overflow-hidden"
      aria-label="Parabolum"
      role="img"
    >
      {/* opaque background — fades during the hand-off */}
      <div
        ref={bgRef}
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
        style={{ background: "#030303", opacity: 1 }}
      />
      {/* very few calm stars */}
      <div ref={starsRef} className="pointer-events-none absolute inset-0" aria-hidden="true">
        {stars.map((s, i) => (
          <span
            key={i}
            className="absolute rounded-full bg-white"
            style={{
              left: `${s.left}%`,
              top: `${s.top}%`,
              width: `${s.size}px`,
              height: `${s.size}px`,
              opacity: s.opacity,
            }}
          />
        ))}
      </div>

      {/* Segmented SVG wordmark — assembles in place, holds, fades out. */}
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        className="relative z-10 w-[88vw] max-w-[940px]"
        aria-hidden="true"
        style={{ willChange: "opacity" }}
      >
        <defs>
          {fragments.map((f) => (
            <clipPath id={f.id} key={f.id} clipPathUnits="userSpaceOnUse">
              <rect x={f.x} y={f.y} width={f.w} height={f.h} />
            </clipPath>
          ))}
        </defs>

        {fragments.map((f, i) => (
          <g
            key={f.id}
            ref={(el) => {
              fragRefs.current[i] = el;
            }}
            /* Inline opacity:0 ensures the first paint is invisible; the
             * useLayoutEffect set() runs synchronously before the next paint,
             * so the assembled wordmark never flashes. */
            style={{ opacity: 0, willChange: "transform, opacity" }}
          >
            <g clipPath={`url(#${f.id})`}>
              <text {...textProps}>{WORD}</text>
            </g>
          </g>
        ))}
      </svg>
    </div>
  );
}
